import { MaybeRefOrGetter } from 'vue'

export enum FilterJoin {
  AND = '&&',
  OR = '||'
}

export type FilterListModel = {
  isPredicate: boolean
  join: FilterJoin
  children: (FilterListModel | FilterPredicate)[]
  filterKey: string
  negate: boolean
}

export type SerializedFilterListModel = {
  isPredicate: boolean
  join: FilterJoin
  children: (SerializedFilterListModel | SerializedFilterPredicate)[]
  filterKey: string
  negate: boolean
}

export enum FilterOperator {
  EQ = '==',
  NEQ = '!=',
  LT = '<',
  GT = '>',
  LTE = '<=',
  GTE = '>=',
  IN = 'in',
  NOT_IN = 'nin',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  CONTAINS = 'contains',
  NULL = '==null',
  NOT_NULL = '!=null'
}

export type FilterValue = string | number | boolean | null | string[]

export type FilterOperatorItem = {
  value: FilterOperator
  messageKey: string
}

export const ALL_OPERATORS = [
  {
    value: FilterOperator.EQ,
    messageKey: 'filter.equals'
  },
  {
    value: FilterOperator.NEQ,
    messageKey: 'filter.not_equals'
  },
  {
    value: FilterOperator.NULL,
    messageKey: 'filter.null'
  },
  {
    value: FilterOperator.NOT_NULL,
    messageKey: 'filter.not_null'
  },
  {
    value: FilterOperator.STARTS_WITH,
    messageKey: 'filter.starts_with'
  },
  {
    value: FilterOperator.ENDS_WITH,
    messageKey: 'filter.ends_with'
  },
  {
    value: FilterOperator.CONTAINS,
    messageKey: 'filter.contains'
  },
  {
    value: FilterOperator.LT,
    messageKey: 'filter.less_than'
  },
  {
    value: FilterOperator.GT,
    messageKey: 'filter.greater_than'
  },
  {
    value: FilterOperator.LTE,
    messageKey: 'filter.less_than_equal'
  },
  {
    value: FilterOperator.GTE,
    messageKey: 'filter.greater_than_equal'
  },
  {
    value: FilterOperator.IN,
    messageKey: 'filter.in'
  },
  {
    value: FilterOperator.NOT_IN,
    messageKey: 'filter.not_in'
  }
]

export type FilterModel = {
  filterKey: string
  filterOperator?: FilterOperator
  filterValue: FilterValue
}

export type FilterFieldOptions = {
  items?: MaybeRefOrGetter<any[]>
  operators: FilterOperatorItem[]

  nullable: boolean
  required: boolean

  type: FilterFieldType

  queryKey: string

  useQueryExpressionLanguage: boolean

  falseNotSupported?: boolean

  textValue: boolean

  itemValue?: string
  itemTitle?: any
}

export enum FilterFieldType {
  TEXT_PATH_FILTER,
  NUMBER_PATH_FILTER,
  BOOLEAN_PATH_FILTER,
  DATE_PATH_FILTER,
  SELECT_PATH_FILTER
}

export type FilterField = {
  valuePath: string
  nullPath: string
  messageKey: string

  options?: FilterFieldOptions
}

export type SerializedField = {
  valuePath: string
}

export type FilterPredicate = {
  filterKey: string
  isPredicate: boolean
  field?: FilterField
  model?: FilterModel
}

export type SerializedFilterPredicate = {
  filterKey: string
  isPredicate: boolean
  field?: SerializedField
  model?: FilterModel
}

export function createStringFilterField(
  path: string,
  messageKey: string,
  nullable: boolean,
  useQueryExpressionLanguage: boolean,
  queryKey: string
): FilterField {
  const nullableOperators = ALL_OPERATORS.filter(
    (op) =>
      op.value == FilterOperator.EQ ||
      op.value == FilterOperator.NEQ ||
      op.value == FilterOperator.STARTS_WITH ||
      op.value == FilterOperator.ENDS_WITH ||
      op.value == FilterOperator.CONTAINS ||
      op.value == FilterOperator.NULL ||
      op.value == FilterOperator.NOT_NULL
  )
  const notNullableOperators = nullableOperators.filter(
    (op) => !(op.value == FilterOperator.NOT_NULL || op.value == FilterOperator.NULL)
  )
  return {
    valuePath: path,
    nullPath: path,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.TEXT_PATH_FILTER,
      required: false,
      nullable: nullable,
      operators: nullable ? nullableOperators : notNullableOperators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      textValue: true
    }
  }
}

export function createStringEqualFilterField(
  path: string,
  messageKey: string,
  nullable: boolean,
  useQueryExpressionLanguage: boolean,
  queryKey: string
): FilterField {
  const operators = ALL_OPERATORS.filter((op) => op.value == FilterOperator.EQ)
  return {
    valuePath: path,
    nullPath: path,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.TEXT_PATH_FILTER,
      required: false,
      nullable: nullable,
      operators: operators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      textValue: true
    }
  }
}

export function createNumberFilterField(
  path: string,
  messageKey: string,
  nullable: boolean,
  useQueryExpressionLanguage: boolean,
  queryKey: string
): FilterField {
  const nullableOperators = ALL_OPERATORS.filter(
    (op) =>
      op.value == FilterOperator.EQ ||
      op.value == FilterOperator.NEQ ||
      op.value == FilterOperator.LT ||
      op.value == FilterOperator.GT ||
      op.value == FilterOperator.LTE ||
      op.value == FilterOperator.GTE ||
      op.value == FilterOperator.NULL ||
      op.value == FilterOperator.NOT_NULL
  )
  const notNullableOperators = nullableOperators.filter(
    (op) => !(op.value == FilterOperator.NOT_NULL || op.value == FilterOperator.NULL)
  )
  return {
    valuePath: path,
    nullPath: path,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.NUMBER_PATH_FILTER,
      required: false,
      nullable: nullable,
      operators: nullable ? nullableOperators : notNullableOperators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      textValue: false
    }
  }
}

export function createBooleanFilterField(
  path: string,
  messageKey: string,
  useQueryExpressionLanguage: boolean,
  queryKey: string,
  falseNotSupported?: boolean
): FilterField {
  const operators = ALL_OPERATORS.filter(
    (op) =>
      op.value == FilterOperator.EQ ||
      (useQueryExpressionLanguage && op.value == FilterOperator.NEQ)
  )
  return {
    valuePath: path,
    nullPath: path,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.BOOLEAN_PATH_FILTER,
      required: false,
      nullable: false,
      operators: operators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      falseNotSupported: falseNotSupported,
      textValue: false
    }
  }
}

export function createDateFilterField(
  path: string,
  messageKey: string,
  nullable: boolean,
  useQueryExpressionLanguage: boolean,
  queryKey: string
): FilterField {
  const nullableOperators = ALL_OPERATORS.filter(
    (op) =>
      op.value == FilterOperator.EQ ||
      op.value == FilterOperator.NEQ ||
      op.value == FilterOperator.LT ||
      op.value == FilterOperator.GT ||
      op.value == FilterOperator.LTE ||
      op.value == FilterOperator.GTE ||
      op.value == FilterOperator.NULL ||
      op.value == FilterOperator.NOT_NULL
  )
  const notNullableOperators = nullableOperators.filter(
    (op) => !(op.value == FilterOperator.NOT_NULL || op.value == FilterOperator.NULL)
  )
  return {
    valuePath: path,
    nullPath: path,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.DATE_PATH_FILTER,
      required: false,
      nullable: nullable,
      operators: nullable ? nullableOperators : notNullableOperators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      textValue: true
    }
  }
}

export function createSelectFilterField(
  path: string,
  nullPath: string,
  messageKey: string,
  nullable: boolean,
  useQueryExpressionLanguage: boolean,
  queryKey: string,
  items: MaybeRefOrGetter<any[]>,
  itemValue: string,
  itemTitle: string | ((item: any) => string),
  textValue: boolean
): FilterField {
  const nullableOperators = ALL_OPERATORS.filter(
    (op) =>
      op.value == FilterOperator.EQ ||
      (useQueryExpressionLanguage && op.value == FilterOperator.NEQ) ||
      (useQueryExpressionLanguage && op.value == FilterOperator.NOT_IN) ||
      op.value == FilterOperator.IN ||
      op.value == FilterOperator.NULL ||
      op.value == FilterOperator.NOT_NULL
  )
  const notNullableOperators = nullableOperators.filter(
    (op) => !(op.value == FilterOperator.NOT_NULL || op.value == FilterOperator.NULL)
  )
  return {
    valuePath: path,
    nullPath: nullPath,
    messageKey: messageKey,
    options: {
      type: FilterFieldType.SELECT_PATH_FILTER,
      required: false,
      nullable: nullable,
      operators: nullable ? nullableOperators : notNullableOperators,
      useQueryExpressionLanguage: useQueryExpressionLanguage,
      queryKey: queryKey,
      items: items,
      itemValue: itemValue,
      itemTitle: itemTitle,
      textValue: textValue
    }
  }
}

export function createGroup(join: FilterJoin, parent?: FilterListModel): FilterListModel {
  const group = {
    filterKey: crypto.randomUUID(),
    children: [],
    join: join,
    negate: false,
    isPredicate: false
  }
  if (parent) {
    parent.children.push(group)
  }
  return group
}

export function addPredicate(group: FilterListModel) {
  const key = crypto.randomUUID()
  const predicate = {
    filterKey: key,
    isPredicate: true,
    model: {
      filterOperator: undefined,
      filterValue: null,
      filterKey: key
    }
  }
  group.children.push(predicate)
}

export function setFieldAndModel(child: FilterPredicate, field: FilterField) {
  child.field = field
  if (field.options!.type == FilterFieldType.TEXT_PATH_FILTER) {
    const defaultOperator = field.options!.operators.some(
      (op) => op.value === FilterOperator.CONTAINS
    )
      ? FilterOperator.CONTAINS
      : field.options!.operators[0]?.value || FilterOperator.EQ
    child.model = {
      filterKey: crypto.randomUUID(),
      filterOperator: defaultOperator,
      filterValue: null
    }
  } else if (field.options!.type == FilterFieldType.NUMBER_PATH_FILTER) {
    child.model = {
      filterKey: crypto.randomUUID(),
      filterOperator: FilterOperator.EQ,
      filterValue: null
    }
  } else if (field.options!.type == FilterFieldType.BOOLEAN_PATH_FILTER) {
    child.model = {
      filterKey: crypto.randomUUID(),
      filterOperator: FilterOperator.EQ,
      filterValue: null
    }
  } else if (field.options!.type == FilterFieldType.DATE_PATH_FILTER) {
    child.model = {
      filterKey: crypto.randomUUID(),
      filterOperator: FilterOperator.EQ,
      filterValue: null
    }
  } else if (field.options!.type == FilterFieldType.SELECT_PATH_FILTER) {
    child.model = {
      filterKey: crypto.randomUUID(),
      filterOperator: FilterOperator.EQ,
      filterValue: null
    }
  }
}

export const EMPTY_OPTIONS: FilterFieldOptions = {
  operators: [],
  nullable: false,
  required: false,
  type: FilterFieldType.SELECT_PATH_FILTER,
  queryKey: '',
  useQueryExpressionLanguage: false,
  textValue: true
}

export function createFilterListModel(
  serializedModel: SerializedFilterListModel,
  fields: FilterField[]
): FilterListModel {
  const fieldsByValuePath = new Map(fields.map((field) => [field.valuePath, field]))

  const hydrateChildren = (
    children: (SerializedFilterListModel | SerializedFilterPredicate)[]
  ): (FilterListModel | FilterPredicate)[] => {
    return children
      .map((child) => {
        if (child.isPredicate) {
          const predicate = child as SerializedFilterPredicate
          // Only include predicates that have a field with a valuePath in the allowed fields
          if (predicate.field?.valuePath && fieldsByValuePath.has(predicate.field.valuePath)) {
            // Replace the predicate.field with the one from fields prop
            const fieldFromProps = fieldsByValuePath.get(predicate.field.valuePath)!
            return {
              ...predicate,
              field: fieldFromProps
            } as FilterPredicate
          }
          return null
        } else {
          // Recursively hydrate children of SerializedFilterListModel
          const listModel = child as SerializedFilterListModel
          const hydratedChildren = hydrateChildren(listModel.children)
          if (hydratedChildren.length > 0) {
            return {
              ...listModel,
              children: hydratedChildren
            } as FilterListModel
          }
          return null
        }
      })
      .filter((child): child is FilterListModel | FilterPredicate => child !== null)
  }

  return {
    isPredicate: false,
    join: serializedModel.join,
    negate: serializedModel.negate,
    filterKey: serializedModel.filterKey,
    children: hydrateChildren(serializedModel.children)
  }
}

export function createSerializedFilterListModel(
  modelValue: FilterListModel
): SerializedFilterListModel {
  const stripChildren = (
    children: (FilterListModel | FilterPredicate)[]
  ): (SerializedFilterListModel | SerializedFilterPredicate)[] => {
    return children.map((child) => {
      if (child.isPredicate) {
        const predicate = child as FilterPredicate
        if (predicate.field) {
          // Create minimal SerializedField with only valuePath
          return {
            ...predicate,
            field: {
              valuePath: predicate.field.valuePath
            } as SerializedField
          } as SerializedFilterPredicate
        }
        return predicate as SerializedFilterPredicate
      } else {
        // Recursively process children of FilterListModel
        const listModel = child as FilterListModel
        return {
          ...listModel,
          children: stripChildren(listModel.children)
        } as SerializedFilterListModel
      }
    })
  }

  return {
    isPredicate: false,
    join: modelValue.join,
    negate: modelValue.negate,
    filterKey: modelValue.filterKey,
    children: stripChildren(modelValue.children)
  }
}
